import type { ReactNode } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { flexRender, type RowData, type Table } from '@tanstack/react-table'
import type { ComponentType } from 'react'
import { Search } from 'lucide-react-native'
import type { LucideProps } from 'lucide-react-native'
import { AdminTablePagination } from './AdminTablePagination'
import { SectionPlaceholder } from '../SectionPlaceholder'
import { SectionErrorState } from '../SectionErrorState'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'center'
  }
}

interface AdminTableProps<T> {
  table: Table<T>
  minWidth?: number
  alignRows?: 'center' | 'top'
  isLoading?: boolean
  isError?: boolean
  isFetching?: boolean
  hasActiveFilters?: boolean
  onRowPress: (row: T) => void
  rowAccessibilityLabel: (row: T) => string
  renderMobileRow: (item: T) => ReactNode
  onRetry?: () => void
  errorMessage?: string
  noResultsDescription?: string
  emptyIcon?: ComponentType<LucideProps>
  emptyTitle?: string
  emptyDescription?: string
  emptyButtonLabel?: string
  onEmptyAction?: () => void
  skeletonRowCount?: number
  page?: number
  pageCount?: number
  totalCount?: number
  onPreviousPage?: () => void
  onNextPage?: () => void
  pageSize?: number
}

function SkeletonRows({ count, columnCount }: { count: number; columnCount: number }) {
  return (
    <View className="mt-5">
      {Array.from({ length: count }).map((_, i) => (
        <View
          key={i}
          className={`min-h-[56px] flex-row items-center py-3 ${i < count - 1 ? 'border-b border-border' : ''}`}
        >
          {Array.from({ length: columnCount }).map((_, ci) => (
            <View key={ci} className="flex-1 gap-1.5 pr-3">
              <View className="h-3 w-3/4 animate-pulse rounded bg-border" />
              {ci === 0 && <View className="h-2.5 w-1/2 animate-pulse rounded bg-border" />}
            </View>
          ))}
        </View>
      ))}
    </View>
  )
}

export function AdminTable<T>({
  table,
  minWidth = 500,
  alignRows = 'center',
  isLoading = false,
  isError = false,
  isFetching = false,
  hasActiveFilters = false,
  onRowPress,
  rowAccessibilityLabel,
  renderMobileRow,
  onRetry,
  errorMessage,
  noResultsDescription,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyButtonLabel,
  onEmptyAction,
  skeletonRowCount = 5,
  page,
  pageCount,
  totalCount,
  onPreviousPage,
  onNextPage,
  pageSize = 25,
}: AdminTableProps<T>) {
  const { t } = useTranslation()
  const rows = table.getRowModel().rows
  const columnCount = table.getAllLeafColumns().length

  function getPaginationProps() {
    if (page !== undefined && pageCount !== undefined && totalCount !== undefined) {
      return {
        page,
        pageCount,
        shouldPaginate: pageCount > 1,
        rangeLabel: t('operations.pagination.range', {
          start: totalCount === 0 ? 0 : (page - 1) * pageSize + 1,
          end: Math.min(page * pageSize, totalCount),
          total: totalCount,
        }),
      }
    }
    const pageIndex = table.getState().pagination.pageIndex
    const ps = table.getState().pagination.pageSize
    const filteredCount = table.getFilteredRowModel().rows.length
    const pc = table.getPageCount()
    return {
      page: pageIndex + 1,
      pageCount: pc,
      shouldPaginate: pc > 1,
      rangeLabel: t('operations.pagination.range', {
        start: filteredCount === 0 ? 0 : pageIndex * ps + 1,
        end: Math.min((pageIndex + 1) * ps, filteredCount),
        total: filteredCount,
      }),
    }
  }

  const paginationProps = getPaginationProps()

  if (isError && errorMessage && onRetry) {
    return (
      <SectionErrorState
        message={errorMessage}
        retryLabel={t('common.retry')}
        onRetry={onRetry}
      />
    )
  }

  if (isLoading) {
    return <SkeletonRows count={skeletonRowCount} columnCount={columnCount} />
  }

  if (rows.length === 0 && hasActiveFilters && noResultsDescription) {
    return (
      <SectionPlaceholder
        Icon={Search}
        title={t('common.noResults')}
        description={noResultsDescription}
      />
    )
  }

  if (rows.length === 0 && emptyIcon && emptyTitle && emptyDescription) {
    return (
      <SectionPlaceholder
        Icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        buttonLabel={emptyButtonLabel}
        onActionPress={onEmptyAction}
      />
    )
  }

  return (
    <View style={{ opacity: isFetching ? 0.5 : 1 }}>
      <View className="lg:hidden">
        <View className="mt-4">
          {rows.map((row, index) => (
            <Pressable
              key={row.id}
              onPress={() => onRowPress(row.original)}
              accessibilityRole="button"
              accessibilityLabel={rowAccessibilityLabel(row.original)}
              className={`py-4 ${index < rows.length - 1 ? 'border-b border-border' : ''}`}
            >
              {renderMobileRow(row.original)}
            </Pressable>
          ))}
        </View>
      </View>

      <View className="hidden lg:flex lg:flex-col">
        <View
          className="mt-5"
          style={{ minWidth, maxWidth: '100%' } as object}
        >
          <View className="min-h-[32px] flex-row items-center border-b border-border pb-1">
            {table.getHeaderGroups()[0]?.headers.map((header, hi, arr) => {
              const align = header.column.columnDef.meta?.align ?? 'left'
              return (
                <View key={header.id} style={{ flex: header.getSize() }} className={hi < arr.length - 1 ? 'pr-3' : ''}>
                  {header.isPlaceholder ? null : (
                    <Text
                      numberOfLines={1}
                      className={`text-xs font-bold uppercase tracking-[0.8px] text-primary ${
                        align === 'center' ? 'text-center' : ''
                      }`}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext()) as string}
                    </Text>
                  )}
                </View>
              )
            })}
          </View>

          {rows.map((row, index) => {
            const isLast = index === rows.length - 1
            return (
              <Pressable
                key={row.id}
                onPress={() => onRowPress(row.original)}
                accessibilityRole="button"
                accessibilityLabel={rowAccessibilityLabel(row.original)}
              >
                <View
                  className={`min-h-[56px] flex-row ${alignRows === 'top' ? 'items-start' : 'items-center'} ${
                    !isLast ? 'border-b border-border' : ''
                  }`}
                >
                  {row.getVisibleCells().map((cell, ci, arr) => (
                    <View
                      key={cell.id}
                      style={{ flex: cell.column.getSize() }}
                      className={`py-2 ${ci < arr.length - 1 ? 'pr-3' : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </View>
                  ))}
                </View>
              </Pressable>
            )
          })}
        </View>
      </View>

      {paginationProps.shouldPaginate && (
        <AdminTablePagination
          page={paginationProps.page}
          pageCount={paginationProps.pageCount}
          rangeLabel={paginationProps.rangeLabel}
          previousLabel={t('operations.pagination.previous')}
          nextLabel={t('operations.pagination.next')}
          onPreviousPage={onPreviousPage ?? (() => table.previousPage())}
          onNextPage={onNextPage ?? (() => table.nextPage())}
          className="pt-4"
        />
      )}
    </View>
  )
}